import { XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { CancelConfirmationDialog } from './CancelConfirmationDialog';

interface CancelButtonProps {
  onConfirm: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'lg';
  className?: string;

  // Context-aware props
  entityType?: 'letter' | 'cheque' | 'contact' | 'item';
  entityName?: string;
  entityId?: string;

  // Custom messaging
  title?: string;
  description?: string;
  buttonText?: string;
}

const CancelButton = ({
  onConfirm,
  disabled = false,
  isLoading = false,
  variant = 'outline',
  size = 'sm',
  className,
  entityType = 'item',
  entityName,
  entityId,
  title,
  description,
  buttonText,
}: CancelButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleConfirm = () => {
    onConfirm();
    setIsDialogOpen(false);
  };

  // Generate context-aware content
  const getEntityDisplayName = () => {
    if (entityName) return entityName;
    if (entityId) return `${entityType} ${entityId.slice(-6)}`;
    return entityType;
  };

  const getButtonText = () => {
    if (buttonText) return buttonText;
    if (entityType === 'item') return 'Cancel';
    return `Cancel ${entityType}`;
  };

  const getDialogTitle = () => {
    if (title) return title;
    const displayName = getEntityDisplayName();
    return `Cancel ${displayName}`;
  };

  const getDialogDescription = () => {
    if (description) return description;

    const entitySpecificWarnings = {
      letter:
        'This will permanently cancel the letter and prevent it from being printed or delivered.',
      cheque:
        'This will permanently cancel the check and prevent it from being printed or delivered.',
      contact: 'This will remove the contact from the system.',
      item: 'This will permanently cancel the item and prevent delivery.',
    };

    return `${entitySpecificWarnings[entityType]} This action cannot be undone.`;
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setIsDialogOpen(true)}
        disabled={disabled || isLoading}
        className={cn(
          'text-red-600 hover:text-red-700 border-red-200 hover:border-red-300',
          variant === 'outline' && 'hover:bg-red-50',
          className,
        )}
      >
        <XMarkIcon className="h-4 w-4 mr-2" />
        {getButtonText()}
      </Button>

      <CancelConfirmationDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onConfirm={handleConfirm}
        isLoading={isLoading}
        title={getDialogTitle()}
        description={getDialogDescription()}
      />
    </>
  );
};

export { CancelButton };
